import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import translations from './locales.js';
//import AttributionsModal from './attributions.js';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Haptics } from '@capacitor/haptics';
import PropTypes from 'prop-types';

const AttributionsModal = lazy(() => import('./attributions.js'));

const showNativeShareSheet = async (fileName) => {
  try {
    const fileUriResult = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Documents,
    });

    const nativeUrl = fileUriResult.uri;

    await Share.share({
      title: 'Export File',
      text: `Here is your exported file: ${fileName}`,
      url: nativeUrl,
      dialogTitle: 'Share your file',
    });
  } catch (err) {
    console.error('Error using native share sheet:', err);
  }
};

const requestStoragePermission = async () => {
  try {
    // Use Filesystem.checkPermissions()
    const status = await Filesystem.checkPermissions();

    if (status.publicStorage === 'granted') { // Check the 'publicStorage' state
      console.log('Storage permission already granted.');
      return true;
    } else if (status.publicStorage === 'prompt' || status.publicStorage === 'prompt-with-rationale') {
      console.log('Requesting storage permission...');
      // Use Filesystem.requestPermissions()
      const result = await Filesystem.requestPermissions();
      if (result.publicStorage === 'granted') { // Check the 'publicStorage' state
        console.log('Storage permission granted by user.');
        return true;
      } else {
        console.warn('Storage permission denied by user.');
        //alert('Storage permission is required to save files.');
        return false;
      }
    } else if (status.publicStorage === 'denied') {
      console.warn('Storage permission permanently denied. Please enable it in app settings.');
      //alert('Storage permission is permanently denied. Please go to app settings to enable it.');
      return false;
    }
  } catch (error) {
    console.error('Error checking or requesting storage permission:', error);
    //alert('An error occurred while checking permissions: ' + error.message);
    return false;
  }
};

// InputNumber Component: This definition must be at the top level of App.js
const InputNumber = ({ name, value, onChange, title }) => {
    const inputRef = useRef(null); // Add this line to create a ref for the input

    return (
        <div className="flex items-center justify-between p-2 my-1 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm transition duration-200 ease-in-out hover:shadow-md hover:scale-[1.01] focus-within:shadow-md focus-within:scale-[1.01] group relative">

            <label className="flex-1 text-gray-700 dark:text-gray-200 max-w-[40%] font-medium">{name}</label>
            <input
                ref={inputRef} // Assign the ref to the input element
                type="number"
                min="0"
                className="w-24 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 text-right text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out hover:border-blue-400"
                value={value}
                onChange={onChange}
            />
            <div className="absolute bottom-full left-1/2 mb-2 w-64 -translate-x-1/2 rounded-md bg-gray-700 dark:bg-gray-900 px-3 py-2 text-center text-sm text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none sm:left-auto sm:right-0 sm:translate-x-0">
                {title}
            </div>

        </div>
    );
}

// Main App Component
function App() {
    // --- STATE MANAGEMENT ---
    // Corresponds to the state variables in your C# application.

    const [showAttributions, setShowAttributions] = useState(false);

    const languageMap = {
        'en': 'English',
        'bg': 'Български',
        'tr': 'Türkçe',
        'fr': 'Français',
        'lv': 'Latviešu',
        'ro': 'Română',
        'sr': 'Српски',
        'ru': 'Русский',
        'de': 'Deutsch',
        'es': 'Español',
        'it': 'Italiano',
        'el': 'Ελληνικά',
        'uk': 'Українська',
        'he': 'עברית',
        'zh': '中文',
        'mk': 'Македонски',
    };

    // NEW: Effect to set initial language based on URL
    useEffect(() => {
        const path = window.location.pathname;
        // Extract the first segment after the base URL, remove leading slash
        const pathSegment = path.split('/')[1]; 
        const defaultLanguage = 'English'; // Fallback if no valid language is found

        if (pathSegment) {
            const mappedLanguage = languageMap[pathSegment.toLowerCase()];
            if (mappedLanguage && translations[mappedLanguage]) {
                setLanguage(mappedLanguage);
            } else {
                setLanguage(defaultLanguage);
            }
        } else {
            setLanguage(defaultLanguage);
        }
    }, []);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // NEW: Ref to hold the current value of isMobileMenuOpen
    //const isMobileMenuOpenRef = useRef(isMobileMenuOpen);

    // NEW: Effect to keep the ref updated whenever isMobileMenuOpen changes
    useEffect(() => {
        isMobileMenuOpenRef.current = isMobileMenuOpen;
    }, [isMobileMenuOpen]); // This effect runs whenever isMobileMenuOpen state changes


    const mainContentRef = useRef(null);


    const isMobileMenuOpenRef = useRef(false); // Initialize with default menu state (closed)

// Add this useEffect to keep the ref updated with the latest state:
useEffect(() => {
    isMobileMenuOpenRef.current = isMobileMenuOpen;
    // console.log("isMobileMenuOpenRef updated to:", isMobileMenuOpen); // Keep for debugging if needed
}, [isMobileMenuOpen]);

    //const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    


    // Effect to handle back button functionality for both web and Capacitor
    // Effect to handle back button functionality for both web and Capacitor
    useEffect(() => {
    const handleBackAction = (ev) => {
        // console.log("Back button pressed. isMobileMenuOpenRef.current:", isMobileMenuOpenRef.current, "Event canGoBack:", ev.canGoBack); // Keep for debugging if needed

        if (isMobileMenuOpenRef.current) {
            // Menu is open. Close it and stop the native back button.
            setIsMobileMenuOpen(false); // Update the state to close the menu

            if (Capacitor.isNativePlatform() && ev && typeof ev.stopPropagation === 'function') {
                ev.stopPropagation(); // Crucial: Stop native back button from exiting app
                // console.log("Capacitor back button propagation stopped."); // Keep for debugging if needed
            }
            // For web, the separate history useEffect handles state popping.
        } else {
            // Menu is not open. Allow default back navigation.
            // For Capacitor, if canGoBack is true, it will navigate back in webview history.
            // If canGoBack is false, it will exit the app (default behavior).
            // For web, the popstate listener handles the browser history navigation.
        }
    };

    let removeCapacitorListener;

    if (Capacitor.isNativePlatform()) {
        const CapacitorAppPlugin = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;

        if (CapacitorAppPlugin && typeof CapacitorAppPlugin.addListener === 'function') {
            removeCapacitorListener = CapacitorAppPlugin.addListener('backButton', handleBackAction);
            // console.log('Capacitor backButton listener added.'); // Keep for debugging if needed
        } else {
            console.error("Capacitor App plugin or addListener method is not available. Back button functionality may not work on native.");
        }
    } else {
        window.addEventListener('popstate', handleBackAction);
        // console.log('Web popstate listener added.'); // Keep for debugging if needed
    }

    return () => {
        if (Capacitor.isNativePlatform() && removeCapacitorListener) {
            removeCapacitorListener.remove();
            // console.log('Capacitor backButton listener removed.'); // Keep for debugging if needed
        } else {
            window.removeEventListener('popstate', handleBackAction);
            // console.log('Web popstate listener removed.'); // Keep for debugging if needed
        }
    };
}, []);

    // *** IMPORTANT ADDITION FOR WEB APP BACK BUTTON BEHAVIOR ***
    // This useEffect will push a new state to the history when the menu opens
    // and replace it when it closes. This is crucial for controlling
    // the browser's back button behavior when the menu is open.
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            if (isMobileMenuOpen) {
                // When menu opens, push a new history state.
                // This makes the browser's back button target this "menu open" state.
                window.history.pushState({ menuOpen: true }, '', window.location.pathname);
                console.log('Pushed history state: menu open');
            } else {
                // When menu closes, if the previous state was 'menu open', pop it.
                // This prevents going back to the 'menu open' state immediately after closing.
                // We need to check if the current state is the one we pushed.
                if (window.history.state && window.history.state.menuOpen) {
                    window.history.back(); // Go back one step, effectively removing our pushed state
                    // We also need to replace the current state to ensure no double back
                    // This can be tricky. A simpler approach for popstate is often:
                    // window.history.replaceState({}, '', window.location.pathname);
                }
                console.log('Replaced/popped history state: menu closed');
            }
        }
    }, [isMobileMenuOpen]);

    const [isMobile, setIsMobile] = useState(document.documentElement.clientWidth <= 853);

    const sidebarRef = useRef(null);

    // Use refs for touch coords to avoid stale closure issues inside addEventListener callbacks
    const sidebarTouchStartXRef = useRef(0);
    const sidebarTouchCurrentXRef = useRef(0);

    // Fix for iOS: attach main content swipe-to-open listeners as passive via useEffect
    // so iOS WebKit knows these handlers will never call preventDefault(), allowing it
    // to resolve taps on child buttons immediately without the 300ms delay.
    useEffect(() => {
        const el = mainContentRef.current;
        if (!el || !isMobile) return;

        const onTouchStart = (e) => {
            if (e.touches[0].clientX < 50) {
                sidebarTouchStartXRef.current = e.touches[0].clientX;
                sidebarTouchCurrentXRef.current = e.touches[0].clientX;
            } else {
                sidebarTouchStartXRef.current = 0;
            }
        };

        const onTouchMove = (e) => {
            if (sidebarTouchStartXRef.current !== 0) {
                sidebarTouchCurrentXRef.current = e.touches[0].clientX;
            }
        };

        const onTouchEnd = () => {
            const swipeThreshold = 75;
            const deltaX = sidebarTouchCurrentXRef.current - sidebarTouchStartXRef.current;
            if (deltaX > swipeThreshold && sidebarTouchStartXRef.current < 50) {
                setIsMobileMenuOpen(true);
            }
            sidebarTouchStartXRef.current = 0;
            sidebarTouchCurrentXRef.current = 0;
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [isMobile]); // re-run if isMobile changes

    // Fix for iOS: attach sidebar swipe-to-close listeners as passive via useEffect
    useEffect(() => {
        const el = sidebarRef.current;
        if (!el) return;

        const sidebarSwipeStartXRef = { current: 0 };
        const sidebarSwipeCurrentXRef = { current: 0 };

        const onTouchStart = (e) => {
            sidebarSwipeStartXRef.current = e.touches[0].clientX;
            sidebarSwipeCurrentXRef.current = e.touches[0].clientX;
        };

        const onTouchMove = (e) => {
            sidebarSwipeCurrentXRef.current = e.touches[0].clientX;
        };

        const onTouchEnd = () => {
            const swipeThreshold = 75;
            const deltaX = sidebarSwipeCurrentXRef.current - sidebarSwipeStartXRef.current;
            if (deltaX < -swipeThreshold) {
                setIsMobileMenuOpen(false);
            }
            sidebarSwipeStartXRef.current = 0;
            sidebarSwipeCurrentXRef.current = 0;
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, []); // sidebar element is stable

    const [touchStartX, setTouchStartX] = useState(0);
    const [touchEndX, setTouchEndX] = useState(0);

    /* eslint-disable react/prop-types, no-unused-vars */
    const handleTouchStart = (e) => {
        setTouchStartX(e.touches[0].clientX);
        setTouchEndX(e.touches[0].clientX); // Initialize end with start
    };
    /* eslint-disable react/prop-types, no-unused-vars */
    const handleTouchMove = (e) => {
        setTouchEndX(e.touches[0].clientX);
    };
    /* eslint-disable react/prop-types, no-unused-vars */
    const handleTouchEnd = () => {
        const threshold = 50; // Minimum swipe distance in pixels to trigger a column change
        const deltaX = touchEndX - touchStartX;

        if (deltaX > threshold) {
            // Swiped right (to show previous column)
            setActiveDeviceColumn(prevCol => Math.max(0, prevCol - 1));
        } else if (deltaX < -threshold) {
            // Swiped left (to show next column)
            setActiveDeviceColumn(prevCol => Math.min(2, prevCol + 1));
        }
        // Reset touch positions
        setTouchStartX(0);
        setTouchEndX(0);
    };

    const [activeDeviceColumn, setActiveDeviceColumn] = useState(0); // 0-based index for device types
    const [isBottomTabsExpanded, setIsBottomTabsExpanded] = useState(false); // State for the expandable bottom section

    // Function to programmatically scroll to a column
    const scrollToColumn = useCallback((columnIndex) => {
        if (mainContentRef.current) {
            const columnWidth = mainContentRef.current.offsetWidth; // Get the width of the container (which is also the width of one column)
            mainContentRef.current.scrollTo({
                left: columnIndex * columnWidth,
                behavior: 'smooth'
            });
            setActiveDeviceColumn(columnIndex); // Update the active button
        }
    }, []); // No dependencies as mainContentRef.current is stable and columnWidth is dynamic

    // Effect to update activeDeviceColumn when user swipes
    const handleScroll = useCallback(() => {
        const scrollContainer = mainContentRef.current;
        if (!scrollContainer) return;

        // Debounce the scroll event to avoid rapid updates
        // This clearTimeout and setTimeout logic needs to be managed outside this useCallback
        // or ensure the timeout ID is managed correctly across renders.
        // For simplicity, let's keep the timeout state within a ref for this specific handler.
        if (mainContentRef.current._scrollTimeout) {
            clearTimeout(mainContentRef.current._scrollTimeout);
        }

        mainContentRef.current._scrollTimeout = setTimeout(() => {
            const scrollLeft = scrollContainer.scrollLeft;
            const columnWidth = scrollContainer.offsetWidth;
            const newActiveColumn = Math.round(scrollLeft / columnWidth);
            setActiveDeviceColumn(newActiveColumn);
        }, 150);
    }, []); // No dependencies needed for this specific logic, as mainContentRef.current is stable

    // Effect to attach and cleanup the scroll listener
    useEffect(() => {
        const scrollContainer = mainContentRef.current;
        if (!scrollContainer) return;

        scrollContainer.addEventListener('scroll', handleScroll);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            // Clear any pending timeout when the component unmounts or effect re-runs
            if (mainContentRef.current && mainContentRef.current._scrollTimeout) {
                clearTimeout(mainContentRef.current._scrollTimeout);
            }
        };
    }, [handleScroll]);


    // Effect to update isMobile state on window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(document.documentElement.clientWidth <= 853); // Update breakpoint here as well
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    /*const translations = {
    English: {
        title: "Loop Calculator", loop: "Loop", totalDevices: "Total devices in loop:", consumption: "Total consumption:",
        crossSection: "Cross-section:", cableLength: "Length of cable:", batteryCapacity: "Capacity of batteries:",
        additionalConsumption: "Additional sounders and other devices consumption:", workHours: "Work hours:",
        save: "Save", load: "Load", new: "New", print: "Print PDF", excel: "Export Excel",
        infoTooltip: "Total devices must not exceed 250. Total consumption must not exceed 700 mA.",
        pdfDevice: "Device", pdfQuantity: "Quantity", pdfCurrent: "Current (mA)",
        pdfReportTitle: "Loop Device Report",
        pdfCableLengthM: "m",
        pdfCrossSectionMM: "mm2",
        pdfBatteryCapacity: "Capacity of batteries:",
        pdfAdditionalConsumption: "Additional sounders and other devices consumption:",
        pdfWorkHours: "Work hours:",
        excelExportTime: "Export Date & Time:",
        excelPanelType: "Panel Type:",
        excelLoopNumber: "Loop Number:",
        excelDeviceName: "Device Name",
        excelQuantity: "Quantity",
        excelConsumption: "Consumption (mA)",
        excelTotalDevicesLoop: "Total Devices (Loop):",
        excelTotalConsumptionLoop: "Total Consumption (Loop):",
        excelLineResistance: "Calculated Line Resistance:",
        excelCableResistance: "Calculated Cable Resistance:",
        excelCableLength: "Calculated Cable Length (based on selected cross-section):",
        excelInputLength: "Input Cable Length:",
        excelSelectedCross: "Selected Cable Cross-Section:",
        excelRequiredCross: "Required Cable Cross-Section (for input length):",
        excelBatteryCalcTitle: "--- Global Battery Life Calculation (All Loops) ---",
        excelSelectedBattery: "Capacity of batteries:",
        excelTotalIdleCons: "Total Idle Consumption (All Loops):",
        excelTotalActiveCons: "Total Active Consumption (All Loops):",
        excelPassiveLife: "Estimated Passive Battery Life (All Loops):",
        inputCrossSectionTitle: "Input Cross-section:",
        inputCableLengthTitle: "Input Cable length:",
        batteryAutonomyTitle: "Battery autonomy:",
        optSmokeDetector: "Addressable Optical smoke detector",
        heatDetector: "Addressable Heat detector",
        optSmokeHeatDetector: "Addressable Optical smoke and Heat detector",
        manualCallPoint: "Addressable Manual Call Point device",
        siren: "Addressable Sounder",
        module: "Addressable Module for conventional zone",
        inOutDevice: "Addressable Input/Output device",
    }}*/

    // Core data: 6 loops, 14 device types per loop
    const initialDevices = () => Array(6).fill(null).map(() => Array(16).fill(0));
    const [devicesLoops, setDevicesLoops] = useState(initialDevices);
    const [currentLoop, setCurrentLoop] = useState(1); // 1-based index

    // UI selections and inputs
    const [centralaType, setCentralaType] = useState('FP9000A');
    const [crossSection, setCrossSection] = useState(0.5);
    const [cableLengthInput, setCableLengthInput] = useState(0);
    const [batteryCapacity, setBatteryCapacity] = useState(4.5);
    const [userCurrent, setUserCurrent] = useState(0);
    const [language, setLanguage] = useState('English');

    // Dark mode: initialize from the class already applied by the inline script in index.html.
    // This guarantees React state always matches what's visually on screen from the start.
    const [darkMode, setDarkMode] = useState(() => {
        return document.documentElement.classList.contains('dark');
    });

    // Keep <html> class and localStorage in sync whenever state changes
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // Follow OS changes only when the user hasn't manually picked a theme
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemChange = (e) => {
            if (!localStorage.getItem('theme')) {
                setDarkMode(e.matches);
            }
        };
        mq.addEventListener('change', handleSystemChange);
        return () => mq.removeEventListener('change', handleSystemChange);
    }, []);

    const toggleDarkMode = () => setDarkMode(prev => !prev);

    // Calculated display values
    const [totalDevices, setTotalDevices] = useState(0);
    const [totalConsumption, setTotalConsumption] = useState(0);
    const [loopIzulators, setLoopIzulators] = useState(0);
    const [calculatedCableLength, setCalculatedCableLength] = useState(0);
    const [calculatedCrossSection, setCalculatedCrossSection] = useState(0);
    const [workHours, setWorkHours] = useState('0h 0m');

    // State for mobile menu visibility

    // --- CONSTANTS AND DATA ---
    // Ported directly from your C# code.

    // PDF Icon component using SVG for better scalability and styling
    const PdfIcon = ({ width = 25, height = 25 }) => (
        <svg width={width} height={height} viewBox="0 0 24 24">
            <g transform="scale(1.4) translate(-4, -3)">
                {/* Document background */}
                <path
                    d="M14,2H6C4.9,2 4,2.9 4,4V20C4,21.1 4.9,22 6,22H18C19.1,22 20,21.1 20,20V8L14,2Z"
                    fill="red"
                />
                {/* Folded corner */}
                <path
                    d="M14,2V8H20L14,2Z"
                    fill="darkred"
                />
                {/* "PDF" text */}
                <text
                    x="50%"
                    y="60%"
                    textAnchor="middle"
                    fill="white"
                    fontSize="8"
                    fontWeight="bold"
                >
                    PDF
                </text>
            </g>
        </svg>
    );

    // Excel Icon component with green styling
    const ExcelIcon = ({ width = 25, height = 25 }) => (
        <svg width={width} height={height} viewBox="0 0 24 24">
            <g transform="scale(1.4) translate(-4, -3)">
                {/* Document background - Green */}
                <path
                    d="M14,2H6C4.9,2 4,2.9 4,4V20C4,21.1 4.9,22 6,22H18C19.1,22 20,21.1 20,20V8L14,2Z"
                    fill="green"
                />
                {/* Folded corner - Darker Green */}
                <path
                    d="M14,2V8H20L14,2Z"
                    fill="darkgreen"
                />
                {/* "XLS" text */}
                <text
                    x="50%"
                    y="60%"
                    textAnchor="middle"
                    fill="white"
                    fontSize="8"
                    fontWeight="bold"
                >
                    XLS
                </text>
            </g>
        </svg>
    );

    const DEVICE_NAMES = [
        "D9000A SR", "D9000AI SR", "D9000A T A1R", "D9000AI T A1R", "D9000A T A1S", "D9000AI T A1S",
        "D9000A MSR", "D9000AI MSR", "D9000A MCP", "S9000A", "S9000AF",
        "M9000A ACZ", "M9000A IO 1/1", "M9000A IO 3/5", "M9000A IN", "M9000A OUT"
    ];

    const WEIGHTS_IDLE = [
        0.5, 0.57, 0.49, 0.55, 0.49, 0.55,  // col 1
        0.51, 0.58, 0.45, 0.48, 0.48,       // col 2
        4, 0.5, 0.75, 0.48, 0.48            // col 3
    ];

    const WEIGHTS_ACTIVE = [
        0.5, 0.57, 0.49, 0.55, 0.49, 0.55,  // col 1
        0.51, 0.58, 0.45, 10, 10,           // col 2
        4, 0.5, 0.75, 0.48, 0.48            // col 3
    ];

    const LEGACY_WEIGHTS_CONSUMPTION = [
        0.5, 0.57, 0.49, 0.55, 0.49, 0.55,  // col 1
        0.51, 0.58, 0.45, 12, 16,       // col 2
        30, 0.5, 0.75, 0.48, 0.48           // col 3
    ];


    const BATTERY_CAPACITIES = {
        '4.5 Ah': 4.5, '5 Ah': 5, '7 Ah': 7, '9 Ah': 9, '12 Ah': 12, '18 Ah': 18
    };

    const CROSS_SECTIONS = [0.5, 0.75, 1.00, 1.50, 2.00, 2.50];

    const englishFallback = {
        title: "Loop Calculator", loop: "Loop", totalDevices: "Total devices in loop:", consumption: "Total consumption:",
        crossSection: "Cross-section:", cableLength: "Length of cable:", batteryCapacity: "Capacity of batteries:",
        additionalConsumption: "Additional sounders and other devices consumption:", workHours: "Work hours:",
        save: "Save", load: "Load", new: "New", print: "Print PDF", excel: "Export Excel",
        infoTooltip: "Total devices must not exceed 250. Total consumption must not exceed 700 mA.",
        pdfDevice: "Device", pdfQuantity: "Quantity", pdfCurrent: "Current (mA)",
        pdfReportTitle: "Loop Device Report",
        pdfCableLengthM: "m",
        pdfCrossSectionMM: "mm2",
        pdfBatteryCapacity: "Capacity of batteries:",
        pdfAdditionalConsumption: "Additional sounders and other devices consumption:",
        pdfWorkHours: "Work hours:",
        excelExportTime: "Export Date & Time:",
        excelPanelType: "Panel Type:",
        excelLoopNumber: "Loop Number:",
        excelDeviceName: "Device Name",
        excelQuantity: "Quantity",
        excelConsumption: "Consumption (mA)",
        excelTotalDevicesLoop: "Total Devices (Loop):",
        excelTotalConsumptionLoop: "Total Consumption (Loop):",
        excelLineResistance: "Calculated Line Resistance:",
        excelCableResistance: "Calculated Cable Resistance:",
        excelCableLength: "Calculated Cable Length (based on selected cross-section):",
        excelInputLength: "Input Cable Length:",
        excelSelectedCross: "Selected Cable Cross-Section:",
        excelRequiredCross: "Required Cable Cross-Section (for input length):",
        excelBatteryCalcTitle: "--- Global Battery Life Calculation (All Loops) ---",
        excelSelectedBattery: "Capacity of batteries:",
        excelTotalIdleCons: "Total Idle Consumption (All Loops):",
        excelTotalActiveCons: "Total Active Consumption (All Loops):",
        excelPassiveLife: "Estimated Passive Battery Life (All Loops):",
        inputCrossSectionTitle: "Input Cross-section:",
        inputCableLengthTitle: "Input Cable length:",
        batteryAutonomyTitle: "Battery autonomy:",
        optSmokeDetector: "Addressable Optical smoke detector",
        heatDetector: "Addressable Heat detector",
        optSmokeHeatDetector: "Addressable Optical smoke and Heat detector",
        manualCallPoint: "Addressable Manual Call Point device",
        siren: "Addressable Sounder",
        module: "Addressable Module for conventional zone",
        inOutDevice: "Addressable Input/Output device",
    }; 
    
    var t = translations[language];

    if(translations==null)
    {
        t = englishFallback;
    }

    // New constant for device tooltips - now dynamically fetched from translations
    const getDeviceTooltips = (t) => [
        t.optSmokeDetector, // 0: D9000A SR
        t.optSmokeDetector, // 1: D9000AI SR
        t.heatDetector,     // 2: D9000A T A1R
        t.heatDetector,     // 3: D9000AI T A1R
        t.heatDetector,     // 4: D9000A T A1S
        t.heatDetector,     // 5: D9000AI T A1S
        t.optSmokeHeatDetector, // 6: D9000A MSR
        t.optSmokeHeatDetector, // 7: D9000AI MSR
        t.manualCallPoint,  // 8: D9000A MCP
        t.siren,            // 9: S9000A
        t.siren,            // 10: S9000AF
        t.module,           // 11: M9000A ACZ
        t.inOutDevice,      // 12: M9000A IO 1/1
        t.inOutDevice,      // 13: M9000A IO 3/5
        t.inOutDevice,      // 14: M9000A IN
        t.inOutDevice       // 15: M9000A OUT
    ];

    // Get device tooltips based on current language
    const DEVICE_TOOLTIPS = getDeviceTooltips(t);


    // --- CORE CALCULATION LOGIC ---
    // This is a port of the `update_labels` and `calcBattery` methods from C#.
    const runCalculations = useCallback(() => {
        const currentLoopData = devicesLoops[currentLoop - 1];
        if (!currentLoopData) {
            setTotalDevices(0);
            setTotalConsumption(0);
            setLoopIzulators(0);
            setCalculatedCableLength(0);
            setCalculatedCrossSection(0);
            setWorkHours('0h 0m');
            return;
        }

        // Isolator device indices: D9000AI SR, D9000AI T A1R, D9000AI T A1S, D9000AI MSR,
        //                          D9000A MCP, S9000A, S9000AF, M9000A ACZ, M9000A IO 1/1,
        //                          M9000A IO 3/5, M9000A IN, M9000A OUT
        const ISOLATOR_INDICES = [1, 3, 5, 7, 8, 9, 10, 11, 12, 13, 14, 15];

        // 1. Calculate total devices and consumption for the CURRENT loop
        let numdev = currentLoopData.reduce((sum, val) => sum + val, 0);
        let curr = currentLoopData.reduce((sum, val, i) => sum + (val * LEGACY_WEIGHTS_CONSUMPTION[i]), 0);
        const numdev_izolator = ISOLATOR_INDICES.reduce((sum, i) => sum + currentLoopData[i], 0);

        setTotalDevices(numdev);
        setTotalConsumption(curr);
        setLoopIzulators(numdev_izolator);

        // 2. Calculate cable length based on consumption
        if (curr > 0 && numdev > 0) {
            const lineResistance = 13 / (curr / 1000);
            const cableResistance = lineResistance - (numdev_izolator * 0.2);
            let len = (cableResistance * crossSection) / 0.0268;
            len = Math.min(len / 2, 2000);
            setCalculatedCableLength(len);
        } else {
            setCalculatedCableLength(0);
        }

        // 3. Calculate cross-section based on input cable length
        if (curr > 0 && numdev > 0 && cableLengthInput > 0) {
            const lineResistance = 13 / (curr / 1000);
            const cableResistance = lineResistance - (numdev_izolator * 0.2);
            const inplen = cableLengthInput * 2;
            const calccross = (0.0268 * inplen) / cableResistance;

            let outcross = 0;
            if (calccross <= 0.5) outcross = 0.5;
            else if (calccross <= 0.75) outcross = 0.75;
            else if (calccross <= 1.0) outcross = 1.0;
            else if (calccross <= 1.5) outcross = 1.5;
            else if (calccross <= 2.0) outcross = 2.0;
            else if (calccross <= 2.5) outcross = 2.5;
            else outcross = -1; // Indicates error or out of range

            setCalculatedCrossSection(outcross);
        } else {
            setCalculatedCrossSection(0);
        }

        // 4. Calculate battery work hours (calcBattery)
        const centrala_1_consume = 100;
        const centrala_6_consume = 260;

        let totalIdleConsumeAllLoops = 0;
        let totalActiveConsumeAllLoops = 0;

        for (let loop = 0; loop < devicesLoops.length; loop++) {
            for (let device = 0; device < devicesLoops[loop].length; device++) {
                const quantity = devicesLoops[loop][device];
                totalIdleConsumeAllLoops += quantity * WEIGHTS_IDLE[device];
                totalActiveConsumeAllLoops += quantity * WEIGHTS_ACTIVE[device];
            }
        }

        // Use the correct centrala consumption based on type
        const centralaConsume = centralaType === 'FP9000A' ? centrala_1_consume : centrala_6_consume;

        // If total idle consumption is zero, it's an error state for work hours
        if (totalIdleConsumeAllLoops + centralaConsume <= 0) { // Added centralaConsume to check
            setWorkHours("error");
            return;
        }

        const totalActiveCurrent = totalActiveConsumeAllLoops + centralaConsume + userCurrent;
        const energyUsedActiveAh = (totalActiveCurrent / 1000.0) * 0.5; // for 0.5h

        const remainingAh = batteryCapacity - energyUsedActiveAh;
        const passiveCurrentA = (totalIdleConsumeAllLoops + centralaConsume) / 1000.0;

        const hours_passive = remainingAh > 0 ? remainingAh / passiveCurrentA : 0;

        const time = TimeSpan.fromHours(hours_passive);
        setWorkHours(`${Math.floor(time.totalHours)}h ${time.minutes}m`);

    }, [devicesLoops, currentLoop, crossSection, cableLengthInput, batteryCapacity, userCurrent, centralaType]);

    // This effect runs the calculations whenever a dependency changes.
    useEffect(() => {
        runCalculations();
    }, [runCalculations]);

    // Prevent sidebar scrolling?
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset'; // Or 'auto' or ''
        }

        // Cleanup function to ensure overflow is reset when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]); // Dependency array: run this effect when isMobileMenuOpen changes

    // --- EVENT HANDLERS ---

    const handleDeviceCountChange = (deviceIndex, rawValue) => {
        const parsed = parseInt(rawValue, 10);
        const safeVal = isNaN(parsed) ? 0 : Math.max(0, parsed);
        const copy = [...devicesLoops];
        copy[currentLoop - 1][deviceIndex] = safeVal;
        setDevicesLoops(copy);
        };

    const handleLoopChange = (loop) => {
        setCurrentLoop(loop);
        setIsMobileMenuOpen(false); // Close menu on loop change
    };

    // Helper function to reset data fields
    const resetData = () => {
        setDevicesLoops(initialDevices());
        setCurrentLoop(1);
        setCrossSection(0.5);
        setCableLengthInput(0);
        setUserCurrent(0);
        // Do NOT reset batteryCapacity here, it's handled by handleCentralaChange
    };

    const handleCentralaChange = (e) => {
        const newType = e.target.value;
        setCentralaType(newType);
        resetData(); // Reset data fields (except batteryCapacity)
        // Set battery capacity based on centrala type, as per C# logic
        if (newType === 'FP9000A-6') {
            setBatteryCapacity(BATTERY_CAPACITIES['9 Ah']); // Default to 9Ah for Centrala 6
        } else {
            setBatteryCapacity(BATTERY_CAPACITIES['4.5 Ah']); // Default to 4.5Ah for FP9000A
        }
        setIsMobileMenuOpen(false); // Close menu on centrala change
    };

    const handleNew = async () => {
        try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
        setCentralaType('FP9000A'); // Reset centrala type to default
        resetData(); // Reset the rest of the data
        setBatteryCapacity(BATTERY_CAPACITIES['4.5 Ah']); // Reset battery to default
        setIsMobileMenuOpen(false); // Close menu on new
    };

    /*const handleSave = () => {
        const dataToSave = {
            DevicesData: devicesLoops,
            CentralaBoxSelectedIndex: centralaType === 'FP9000A' ? 0 : 1,
            CurrentLoopIndex: currentLoop,
            CableLength: cableLengthInput,
            crossboxIndex: CROSS_SECTIONS.indexOf(crossSection),
            langIndex: Object.keys(translations).indexOf(language),
            batteryBoxIndex: Object.values(BATTERY_CAPACITIES).indexOf(batteryCapacity),
            userCurrent: userCurrent.toString(),
        };

        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
            JSON.stringify(dataToSave, null, 2)
        )}`;
        const link = document.createElement("a");
        link.href = jsonString;
        link.download = "project.json";
        link.click();
        setIsMobileMenuOpen(false); // Close menu on save
    };*/

      const handleSave = async () => {
        try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
    const dataToSave = {
        DevicesData: devicesLoops,
        CentralaBoxSelectedIndex: centralaType === 'FP9000A' ? 0 : 1,
        CurrentLoopIndex: currentLoop,
        CableLength: cableLengthInput,
        crossboxIndex: CROSS_SECTIONS.indexOf(crossSection),
        langIndex: Object.keys(translations).indexOf(language),
        batteryBoxIndex: Object.values(BATTERY_CAPACITIES).indexOf(batteryCapacity),
        userCurrent: userCurrent.toString(),
    };

    const jsonString = JSON.stringify(dataToSave, null, 2);
    const fileName = `project_${new Date().getTime()}.json`;

    // --- Conditional Logic for Web vs. Native ---
    if (Capacitor.getPlatform() !== 'web') {
        console.log(`Running in native Capacitor environment. Attempting native save.`);

        // The permission check is correct and doesn't need to be changed.
        const hasPermission = await requestStoragePermission();
        if (!hasPermission) {
            console.warn('Native save aborted: Storage permission not granted.');
            setIsMobileMenuOpen(false);
            return;
        }

        try {
            // --- CORRECTED CODE ---
            // 1. Do NOT manually encode to Base64.
            // 2. Pass the raw jsonString directly to the 'data' property.
            // 3. Ensure encoding is set to Encoding.UTF8.
            await Filesystem.writeFile({
                path: fileName,
                data: jsonString, // Use the raw JSON string here
                directory: Directory.Documents,
                encoding: Encoding.UTF8, // Tell the Filesystem the data is a UTF-8 string
            });

            //alert('Project data saved successfully to Documents folder!');
            console.log('JSON data saved successfully natively to:', Directory.Documents, '/', fileName);
        } catch (e) {
            console.error('Error saving JSON data natively:', e);
            //alert('Failed to save project data: ' + e.message);
        }

    } else {
        // Web browser logic (this part is fine and doesn't need to change)
        console.log('Running in web browser. Attempting browser download.');
        const dataUri = `data:text/json;charset=utf-8,${encodeURIComponent(jsonString)}`;
        const link = document.createElement("a");
        link.href = dataUri;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('JSON data saved successfully via browser download!');
    }

    setIsMobileMenuOpen(false);
};

    const handleLoad = async (event) => {
        try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
        const fileReader = new FileReader();
        fileReader.readAsText(event.target.files[0], "UTF-8");
        fileReader.onload = e => {
            try {
                const loadedData = JSON.parse(e.target.result);

                // Validate and set data
                if (loadedData.DevicesData && Array.isArray(loadedData.DevicesData)) {
                    setDevicesLoops(loadedData.DevicesData);
                }
                if (loadedData.CurrentLoopIndex) {
                    setCurrentLoop(loadedData.CurrentLoopIndex);
                }
                const newCentralaType = loadedData.CentralaBoxSelectedIndex === 0 ? 'FP9000A' : 'FP9000A-6';
                setCentralaType(newCentralaType);

                if (loadedData.crossboxIndex != null) {
                    setCrossSection(CROSS_SECTIONS[loadedData.crossboxIndex] || 0.5);
                }
                if (loadedData.batteryBoxIndex != null) {
                    // Ensure the battery capacity is valid for the selected centrala type
                    const loadedBatteryValue = Object.values(BATTERY_CAPACITIES)[loadedData.batteryBoxIndex];
                    if (newCentralaType === 'FP9000A' && loadedBatteryValue <= 9) {
                        setBatteryCapacity(loadedBatteryValue);
                    } else if (newCentralaType === 'FP9000A-6' && loadedBatteryValue >= 9) {
                        setBatteryCapacity(loadedBatteryValue);
                    } else {
                        // Fallback to default if loaded battery is incompatible with centrala type
                        setBatteryCapacity(newCentralaType === 'FP9000A-6' ? BATTERY_CAPACITIES['9 Ah'] : BATTERY_CAPACITIES['4.5 Ah']);
                    }
                }
                setCableLengthInput(parseFloat(loadedData.CableLength) || 0); // Ensure parsing as float
                setUserCurrent(parseFloat(loadedData.userCurrent) || 0);
                setLanguage(Object.keys(translations)[loadedData.langIndex] || 'English');

            } catch (error) {
                console.error("Error parsing JSON file:", error);
                // Avoid alert() in production apps
            }
        };
        // Clear the input value to allow loading the same file again
        event.target.value = null;
        setIsMobileMenuOpen(false); // Close menu on load
    };

        // --- PDF GENERATION LOGIC ---
    const generatePdfReport = useCallback(async () => { // Made async to await permissions and file write
        // Ensure jsPDF is available globally (loaded via CDN)
        if (typeof window.jspdf === 'undefined' || typeof window.jspdf.jsPDF === 'undefined') {
            console.error("jsPDF library not loaded. Please ensure the CDN script tags are in your HTML.");
            return;
        }

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();

        // --- Set the font for Cyrillic support (still needed if any non-Latin characters appear, e.g., in device names) ---
        // If you were having issues with NotoSans, you might temporarily comment this out
        // or ensure your NotoSans-normal.js is loaded correctly.
        // If you are SURE only ASCII characters will ever appear, you can remove this font setting.
        // However, it's safer to keep it if there's any chance of non-ASCII characters.
        //doc.setFont('NotoSans', 'normal');

        // --- Logo and Date/Time for Headers (Calculated once) ---
        // Replace with your actual base64 logo string
        const base64Logo = 'data:image/jpeg;base64,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ấmWawQU5SZU72tBikkEFIRDnIf8AE2btH+gZRG7A1gpPEC54iFybgym/VcIgiIk7u4OtiEoDI2nsUW7QuYRCrBNiiVmWwAgDp/0P/9oACAECAwE/EP8AnjQnQUSnBKiBlExodd6AOFYTtRdP4ClS263StzWvqivqivqivqivqqin+AUFkBTCTxHupPgKEozycsUlbDP8AlFFsPBQMwgvBHFv/AEgRREVmYky/e7oHusln0H4/dBcetPPfd5cqMkk7D8FSgQGRUdJt/juziiogyWwcFxLQRRK8kjzSKhzKipYM2guWYW2aTGe1009V96/FfatPvWn3rSKpE5vxQdAPNfivvWn3r8V96/FfevxSASBlOrHKvvX4r71+K+9fivvX4p/qvxTkm5n4a+1fiijipNp+DAGIBN1mlmfa/FcE/FJoQStqMEHregQS4taaywsbD+amC6i34OubS8GzO8nzhDJSnSt/4/tIGkGMOWs3/OChM5FPqE/VCI1BwLm0zOzeh+NzYyKWCgtO9bTjtQhcAPmlGQJa2nv4o+BFQA5Ap9ki/HOvrSn1pT60oUbZt4g+aiiAqpNvsr60p9aULQItIfNXHm3wWPc1Fci5jxb3TCM0HmgNysrmVEHVthGGxHSpMHUcXWouWc5vso2BXV8b96AnVeEue4JbrSQW+a5cigVtrUM6BRK6Kb4XDpYwl2rbzhHm4wAMxdwEcsLYdX8YIBqChAZFWjm47F8Id0+r3grNPQf1KFSIM4V9GomcmYEwZ1KvN+ix7w2F+D+4ooM1KETQFcywt10opIyt0P7Q2QW2wiHrd/X7wD/kClHAkWItRODWm50B/NN6BFgW7c6IQNRSaM5ZMqg2hfxhpKrq+a6vmg+TgYHS710wh7vEYrll/aQTdj3hLtCx2wvHI+22EWcj7bvqMJAzcdj+4HjYtYQ36V1eHWt7j3oMzqGbjOiI5AKlJzUrLc7Pmtp5K2XkqEK9z2/uEC6pe336o2COQiglElpVy674SMysdv7hc1Ty442oYug5UXKtn94dqFBojRBpUMglytTTi2qQCYgj3W74Vuvit3wrd8K2fCmEFeKYXFwEU2uNbvhRElWal7TLW74VIXTFra0s54WiE5LYyyrfeVOpqXC4PkeXOt95VuvKt35VvvKmF2bZ59+hWcQbT4/tb7yrfeVb7ypisp6NJhf5PVyHNa33lUoJBBw4T+Wc2f3QUosMWc9KWWXP/of//Z'; // Replace with your actual base64 logo string

        const originalLogoWidth = 361; // Replace with your logo's original width
        const originalLogoHeight = 64; // Replace with your logo's original height

        const desiredPdfWidth = 60;
        const aspectRatio = originalLogoWidth / originalLogoHeight;
        const calculatedPdfHeight = desiredPdfWidth / aspectRatio;

        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const formattedDateTime = `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;

        let yOffset = 45;

        // --- Use English translations directly for PDF output ---
        const englishTranslations = translations.English;

        const drawPageHeaderAndFooter = (data) => {
            if (base64Logo !== 'YOUR_BASE64_ENCODED_LOGO_STRING_HERE') {
                doc.addImage(base64Logo, 'JPEG', 15, 10, desiredPdfWidth, calculatedPdfHeight);
            } else {
                doc.setFontSize(10);
                doc.text("Your Company Logo Here (Please replace with Base64)", 15, 20);
            }
            doc.setFontSize(10);
            // doc.setFont('NotoSans', 'normal'); // Re-enable if NotoSans is properly loaded and needed for PDF
            doc.text(formattedDateTime, 180, 20, { align: 'right' });
            doc.setFontSize(16);
            doc.text(`${englishTranslations.pdfReportTitle} - ${centralaType}`, 105, 30, { align: 'center' });
            doc.line(15, 35, 195, 35);

            let pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.text('Page ' + doc.internal.getCurrentPageInfo().pageNumber + ' of ' + pageCount, data.settings.margin.left, doc.internal.pageSize.height - 10);
        };

        drawPageHeaderAndFooter({ settings: { margin: { left: 15 } } });


        devicesLoops.forEach((loopData, loopIndex) => {
            const loopNum = loopIndex + 1;
            let currentLoopTotalDevices = 0;
            let currentLoopTotalConsumption = 0;
            let currentLoopIzulators = 0;

            loopData.forEach((quantity, deviceIndex) => {
                currentLoopTotalDevices += quantity;
                currentLoopTotalConsumption += quantity * LEGACY_WEIGHTS_CONSUMPTION[deviceIndex];
                if ([3, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(deviceIndex)) {
                    currentLoopIzulators += quantity;
                }
            });

            if (currentLoopTotalDevices > 0) {
                doc.setFontSize(14);
                // doc.setFont('NotoSans', 'normal'); // Re-enable if NotoSans is properly loaded and needed for PDF
                doc.text(`${englishTranslations.loop} ${loopNum}`, 15, yOffset);
                yOffset += 10;

                const tableHeaders = [[englishTranslations.pdfDevice, englishTranslations.pdfQuantity, englishTranslations.pdfCurrent]];
                const tableRows = loopData.map((quantity, i) => [
                    DEVICE_NAMES[i],
                    quantity.toString(),
                    (quantity * LEGACY_WEIGHTS_CONSUMPTION[i]).toFixed(2)
                ]);

                doc.autoTable({
                    startY: yOffset,
                    head: tableHeaders,
                    body: tableRows,
                    theme: 'grid',
                    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' /*, font: 'NotoSans', fontStyle: 'normal' */ }, // Re-enable font if NotoSans is properly loaded
                    headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' /*, font: 'NotoSans' */ }, // Re-enable font if NotoSans is properly loaded
                    margin: { left: 15, right: 15 },
                    didDrawPage: drawPageHeaderAndFooter,
                    didParseCell: function(data) {
                        if (data.section === 'head') {
                            data.cell.styles.startY = 40;
                        }
                    }
                });

                yOffset = doc.autoTable.previous.finalY + 5;

                const loopLineResistance = currentLoopTotalConsumption > 0 ? 13 / (currentLoopTotalConsumption / 1000) : 0;
                const loopCableResistance = loopLineResistance - (currentLoopIzulators * 0.2);

                let calculatedLen = 0;
                if (currentLoopTotalConsumption > 0 && currentLoopTotalDevices > 0) {
                    let len = (loopCableResistance * crossSection) / 0.0268;
                    calculatedLen = Math.min(len / 2, 2000);
                }

                let outputCrossSection = 0.5;
                if (currentLoopTotalConsumption > 0 && currentLoopTotalDevices > 0 && cableLengthInput > 0) {
                    const inplen = cableLengthInput * 2;
                    const calcedcross = (0.0268 * inplen) / loopCableResistance;
                    if (calcedcross > 0 && calcedcross <= 0.5) outputCrossSection = 0.5;
                    else if (calcedcross > 0.5 && calcedcross <= 0.75) outputCrossSection = 0.75;
                    else if (calcedcross > 0.75 && calcedcross <= 1.00) outputCrossSection = 1.00;
                    else if (calcedcross > 1 && calcedcross <= 1.5) outputCrossSection = 1.5;
                    else if (calcedcross > 1.5 && calcedcross <= 2) outputCrossSection = 2;
                    else if (calcedcross > 2) outputCrossSection = 2.5;
                }

                doc.setFontSize(10);
                // doc.setFont('NotoSans', 'normal'); // Re-enable if NotoSans is properly loaded and needed for PDF
                doc.text(`${englishTranslations.totalDevices} ${currentLoopTotalDevices}`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.consumption} ${currentLoopTotalConsumption.toFixed(2)} mA`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.crossSection} ${crossSection.toFixed(2)} ${englishTranslations.pdfCrossSectionMM}`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.cableLength} ${calculatedLen.toFixed(2)} ${englishTranslations.pdfCableLengthM}`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.cableLength} ${cableLengthInput.toFixed(2)} ${englishTranslations.pdfCableLengthM} (Input)`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.crossSection} ${outputCrossSection.toFixed(2)} ${englishTranslations.pdfCrossSectionMM} (Calculated)`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.pdfBatteryCapacity} ${batteryCapacity} Ah`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.additionalConsumption} ${userCurrent.toFixed(2)} mA`, 15, yOffset);
                yOffset += 7;
                doc.text(`${englishTranslations.workHours} ${workHours}`, 15, yOffset);
                yOffset += 15;

                if (loopIndex < devicesLoops.length - 1) {
                    doc.addPage();
                    yOffset = 45;
                }
            }
        });

        // --- Conditional Logic for Web vs. Native ---
        const pdfOutput = doc.output('datauristring'); // Get PDF as data URI string
        const base64Pdf = pdfOutput.split(',')[1]; // Extract base64 part

        const fileName = `Loop_Device_Report_${new Date().getTime()}.pdf`;

        if (Capacitor.getPlatform() !== 'web') {
            console.log(`Running in native Capacitor environment. Attempting native PDF save.`);
            const hasPermission = await requestStoragePermission();
            if (!hasPermission) {
                console.warn('Native PDF save aborted: Storage permission not granted.');
                setIsMobileMenuOpen(false);
                return;
            }

            try {
                await Filesystem.writeFile({
                    path: fileName,
                    data: base64Pdf, // Use the base64 string here
                    directory: Directory.Documents,
                    encoding: Encoding.Base64, // Specify that the data is Base64 encoded
                });
                //alert('PDF report saved successfully to Documents folder!');
                console.log('PDF report saved successfully natively to:', Directory.Documents, '/', fileName);
                if (window.confirm(t.askShareExportedFile)) {
                    await showNativeShareSheet(fileName);
                }
            } catch (e) {
                console.error('Error saving PDF natively:', e);
                //alert('Failed to save PDF report: ' + e.message);
            }
        } else {
            // Web browser logic
            console.log('Running in web browser. Attempting browser PDF download.');
            doc.save(fileName);
            console.log('PDF report saved successfully via browser download!');
        }
        setIsMobileMenuOpen(false); // Close menu on print
    }, [devicesLoops, centralaType, crossSection, cableLengthInput, batteryCapacity, userCurrent, workHours, translations]);


    const handlePrint = async () => {
        try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
        generatePdfReport();
    };

        // --- EXCEL GENERATION LOGIC ---
    const handleExportExcel = useCallback(async () => { // Made async to await permissions and file write
        // Ensure XLSX library is available globally (loaded via CDN)
        try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
        if (typeof window.XLSX === 'undefined') {
            console.error("XLSX library not loaded. Please ensure the CDN script tag is in your HTML.");
            return;
        }

        const wb = window.XLSX.utils.book_new(); // Create a new workbook
        const now = new Date();
        const formattedDateTime = now.toLocaleString(); // Use locale-specific date/time

        const centrala_1_consume = 100;
        const centrala_6_consume = 260;

        // Calculate global battery values once
        let totalIdleConsumeAllLoops = 0;
        let totalActiveConsumeAllLoops = 0;
        for (let loop = 0; loop < devicesLoops.length; loop++) {
            for (let device = 0; device < devicesLoops[loop].length; device++) {
                const quantity = devicesLoops[loop][device];
                totalIdleConsumeAllLoops += quantity * WEIGHTS_IDLE[device];
                totalActiveConsumeAllLoops += quantity * WEIGHTS_ACTIVE[device];
            }
        }
        const centralaConsume = centralaType === 'FP9000A' ? centrala_1_consume : centrala_6_consume;
        const totalActiveCurrentGlobal = totalActiveConsumeAllLoops + centralaConsume + userCurrent;
        const energyUsedActiveAhGlobal = (totalActiveCurrentGlobal / 1000.0) * 0.5;
        const remainingAhGlobal = batteryCapacity - energyUsedActiveAhGlobal;
        const passiveCurrentAGlobal = (totalIdleConsumeAllLoops + centralaConsume) / 1000.0;
        const hours_passiveGlobal = remainingAhGlobal > 0 ? remainingAhGlobal / passiveCurrentAGlobal : 0;
        const timeGlobal = TimeSpan.fromHours(hours_passiveGlobal);
        const workHoursGlobal = `${Math.floor(timeGlobal.totalHours)}h ${timeGlobal.minutes}m`;


        devicesLoops.forEach((loopData, loopIndex) => {
            const loopNum = loopIndex + 1;
            let currentLoopTotalDevices = 0;
            let currentLoopTotalConsumption = 0;
            let currentLoopIzulators = 0;

            loopData.forEach((quantity, deviceIndex) => {
                currentLoopTotalDevices += quantity;
                currentLoopTotalConsumption += quantity * LEGACY_WEIGHTS_CONSUMPTION[deviceIndex];
                if ([3, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(deviceIndex)) {
                    currentLoopIzulators += quantity;
                }
            });

            // Only create a sheet if the loop has devices
            if (currentLoopTotalDevices > 0) {
                let ws_data = [];
                
                // Header Information
                ws_data.push([t.excelExportTime, formattedDateTime]);
                ws_data.push([t.excelPanelType, centralaType]);
                ws_data.push([t.excelLoopNumber, loopNum.toString()]);
                ws_data.push([]); // Blank row for spacing
                ws_data.push([]); // Another blank row for spacing

                // Device Table Header
                const deviceTableHeaderIndex = ws_data.length; // Store index for styling later
                ws_data.push([t.excelDeviceName, t.excelQuantity, t.excelConsumption]);

                // Populate Device Table
                loopData.forEach((quantity, i) => {
                    ws_data.push([
                        DEVICE_NAMES[i],
                        quantity,
                        (quantity * LEGACY_WEIGHTS_CONSUMPTION[i]).toFixed(2)
                    ]);
                });
                ws_data.push([]); // Blank row after table

                // Calculated Data for the Current Loop
                const loopLineResistance = currentLoopTotalConsumption > 0 ? 13 / (currentLoopTotalConsumption / 1000) : 0;
                const loopCableResistance = loopLineResistance - (currentLoopIzulators * 0.2);

                let calculatedLen = 0;
                if (currentLoopTotalConsumption > 0 && currentLoopTotalDevices > 0) {
                    let len = (loopCableResistance * crossSection) / 0.0268;
                    calculatedLen = Math.min(len / 2, 2000);
                }

                let outputCrossSection = 0.5;
                if (currentLoopTotalConsumption > 0 && currentLoopTotalDevices > 0 && cableLengthInput > 0) {
                    const inplen = cableLengthInput * 2;
                    const calcedcross = (0.0268 * inplen) / loopCableResistance;
                    if (calcedcross <= 0.5) outputCrossSection = 0.5;
                    else if (calcedcross <= 0.75) outputCrossSection = 0.75;
                    else if (calcedcross <= 1.0) outputCrossSection = 1.0;
                    else if (calcedcross <= 1.5) outputCrossSection = 1.5;
                    else if (calcedcross <= 2.0) outputCrossSection = 2.0;
                    else if (calcedcross <= 2.5) outputCrossSection = 2.5;
                    else outputCrossSection = -1; // Indicates error or out of range
                }


                ws_data.push([t.excelTotalDevicesLoop, `${currentLoopTotalDevices} / 250`]);
                ws_data.push([t.excelTotalConsumptionLoop, `${currentLoopTotalConsumption.toFixed(2)} mA / 700 mA`]);
                ws_data.push([t.excelLineResistance, `${loopLineResistance.toFixed(2)} Ω`]);
                ws_data.push([t.excelCableResistance, `${loopCableResistance.toFixed(2)} Ω`]);
                ws_data.push([t.excelSelectedCross, `${crossSection.toFixed(2)} mm²`]);
                ws_data.push([t.excelCableLength, `${calculatedLen.toFixed(2)} m`]);
                ws_data.push([t.excelInputLength, `${cableLengthInput.toFixed(2)} m`]);
                ws_data.push([t.excelRequiredCross, `${outputCrossSection === -1 ? '-' : outputCrossSection.toFixed(2)} mm²`]);
                ws_data.push([]); // Blank row for spacing

                // Global Battery Calculation (All Loops)
                ws_data.push([t.excelBatteryCalcTitle]);
                ws_data.push([t.excelSelectedBattery, `${batteryCapacity} Ah`]);
                ws_data.push([t.excelTotalIdleCons, `${totalIdleConsumeAllLoops.toFixed(2)} mA`]);
                ws_data.push([t.excelTotalActiveCons, `${totalActiveConsumeAllLoops.toFixed(2)} mA`]);
                ws_data.push([t.excelPassiveLife, workHoursGlobal]);
                ws_data.push([]);


                const ws = window.XLSX.utils.aoa_to_sheet(ws_data);

                // Apply basic styling (bold headers)
                // Note: XLSX.js styling is limited for direct sheet creation.
                // For more advanced styling, you'd typically use a library like 'exceljs'
                // or generate XML manually, but for simple bolding, this is fine.
                // This is a simplified approach for demonstration.
                ws['!cols'] = [
                    { wch: 40 }, // Column A (Device Name)
                    { wch: 20, alignment: { horizontal: "left" } }, // Column B (Quantity)
                    { wch: 20 }  // Column C (Consumption)
                ];

                // Style the main header information
                ['A', 'B'].forEach(col => {
                    for (let i = 1; i <= 3; i++) { // Rows 1, 2, 3 for header info
                        const cellRef = col + i;
                        if (ws[cellRef]) {
                            ws[cellRef].s = { font: { bold: true } };
                        }
                    }
                });

                // Style the device table header
                if (deviceTableHeaderIndex >= 0) {
                    ['A', 'B', 'C'].forEach(col => {
                        const cellRef = col + (deviceTableHeaderIndex + 1); // +1 because Excel is 1-indexed
                        if (ws[cellRef]) {
                            ws[cellRef].s = { font: { bold: true }, fill: { fgColor: { rgb: "FFD3D3D3" } } }; // Light gray background
                        }
                    });
                }
                
                // Style the global battery calculation title
                const batteryTitleRow = ws_data.findIndex(row => row[0] === t.excelBatteryCalcTitle) + 1;
                if (batteryTitleRow > 0) {
                    ['A'].forEach(col => {
                        const cellRef = col + batteryTitleRow;
                        if (ws[cellRef]) {
                            ws[cellRef].s = { font: { bold: true }, alignment: { horizontal: "right" } };
                        }
                    });
                }


                window.XLSX.utils.book_append_sheet(wb, ws, `Loop ${loopNum}`);
            }
        });

        // If no loops had devices, create a default empty sheet or notify user
        if (wb.SheetNames.length === 0) {
            const ws = window.XLSX.utils.aoa_to_sheet([[t.excelExportTime, formattedDateTime], ["No devices entered for any loop."]]);
            window.XLSX.utils.book_append_sheet(wb, ws, "Summary");
        }

        // --- Conditional Logic for Web vs. Native ---
        const fileName = `Loop_Device_Report_${now.toISOString().slice(0, 10)}.xlsx`;

        if (Capacitor.getPlatform() !== 'web') {
            console.log(`Running in native Capacitor environment. Attempting native Excel save.`);
            const hasPermission = await requestStoragePermission();
            if (!hasPermission) {
                console.warn('Native Excel save aborted: Storage permission not granted.');
                setIsMobileMenuOpen(false);
                return;
            }

            try {
                // Generate the Excel file as a binary string
                const wbout = window.XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });

                await Filesystem.writeFile({
                    path: fileName,
                    data: wbout, // Use the base64 string here
                    directory: Directory.Documents,
                    encoding: Encoding.Base64, // Specify that the data is Base64 encoded
                });
                //alert('Excel report saved successfully to Documents folder!');
                console.log('Excel report saved successfully natively to:', Directory.Documents, '/', fileName);
                if (window.confirm(t.askShareExportedFile)) {
                    await showNativeShareSheet(fileName);
                }
            } catch (e) {
                console.error('Error saving Excel natively:', e);
                //alert('Failed to save Excel report: ' + e.message);
            }
        } else {
            // Web browser logic
            console.log('Running in web browser. Attempting browser Excel download.');
            window.XLSX.writeFile(wb, fileName);
            console.log('Excel report saved successfully via browser download!');
        }
        setIsMobileMenuOpen(false); // Close menu on excel export
    }, [devicesLoops, centralaType, crossSection, cableLengthInput, batteryCapacity, userCurrent, translations, t]);


    // --- RENDER ---
    return (
        // The main container for the application, using Tailwind CSS for responsive centering and padding.
        // Changed overall background to a softer gray for a modern feel.
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-2 font-inter antialiased flex flex-col items-center" style={{ overflowX: 'clip' }}>
            {/* Tailwind CSS is loaded via CDN in the HTML file for simplicity. */}
            <script src="https://cdn.tailwindcss.com"></script>
            {/* Ensure Inter font is loaded for consistent typography */}
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            {/* Include SheetJS (xlsx) via CDN for Excel export */}
            <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
            {/* Include jsPDF via CDN for PDF export */}
            <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
            <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.25/jspdf.plugin.autotable.min.js"></script>

            {/* Main application container with a max-w-4xl for readability on large screens */}
            {/* Changed main container background to white for clear contrast with page background. */}
            <div
                className="w-full max-w-full sm:max-w-4xl bg-white dark:bg-gray-800 shadow-lg rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6 relative"
            >

                {/* Header Section */}
                <header className="z-10 flex-wrap flex items-center justify-between sm:justify-start bg-gradient-to-r from-blue-700 to-blue-900 text-white p-4 rounded-lg shadow-xl relative">
                    
                    <div className="flex-none transition-transform duration-300 hover:scale-110">
                        <img
                            src="/dmtech.jpg"
                            alt="DMTech Ltd"
                            className="h-8 w-auto rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition duration-300 ease-in-out"
                        />
                    </div>

                    {/* Hamburger Menu Button (visible on mobile) */}
                    <button
                        className="sm:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer active:bg-blue-600 active:scale-95 transition duration-150"
                        onClick={async () => {
                            // Vibrate the phone for 25ms
                            try { Haptics.vibrate({ duration: 25 }); } catch (_) { /* Haptics unavailable on web */ }
                            // Toggle the mobile menu state
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                        }}
                    >
                        <svg
                            className="h-6 w-6 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            {isMobileMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16m-7 6h7"
                                />
                            )}
                        </svg>
                    </button>

                    {/* Desktop Navigation (hidden on mobile) */}
                <div className="hidden sm:flex flex-1 justify-center">
                    <div className="flex flex-wrap md:space-y-2 lg:space-y-0 items-center lg:space-x-4 md:space-x-10">
                        <select
                            className="sm:ml-10 md:ml-10 lg:ml-0 bg-blue-900 text-white py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-300 ease-in-out hover:bg-blue-800"
                            value={centralaType}
                            onChange={handleCentralaChange}
                        >
                            <option value="FP9000A">FP9000A</option>
                            <option value="FP9000A-6">FP9000A-6</option>
                        </select>
                        <span className="text-xl font-semibold">{t.loop}: {currentLoop}</span>
                        <div className="flex flex-wrap space-x-2">

                            {/* New Button */}
                            <button
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-gray-100 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 cursor-pointer transition duration-200 ease-in-out group" // Added 'group' class
                                onClick={handleNew}
                                title={t.new}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 group-hover:rotate-12 transition-transform duration-200" // Added hover effect
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M7 21h10a2 2 0 002-2V7l-6-6H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M13 3v6h6"
                                    />
                                </svg>
                            </button>

                            {/* Load/Open Button */}
                            <label
                                htmlFor="file-upload"
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-gray-100 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 cursor-pointer transition duration-200 ease-in-out group" // Added 'group' class
                                title={t.load}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 group-hover:rotate-12 transition-transform duration-200" // Added hover effect
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 7a2 2 0 012-2h6l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                                    />
                                </svg>
                            </label>
                            <input
                                id="file-upload"
                                type="file"
                                accept=".json"
                                onChange={handleLoad}
                                className="hidden"
                            />

                            {/* Save Button */}
                            <button
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-gray-100 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 cursor-pointer transition duration-200 ease-in-out group" // Added 'group' class
                                onClick={handleSave}
                                title={t.save}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 group-hover:rotate-12 transition-transform duration-200" // Added hover effect
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M17 21v-8H7v8M7 3v5h8V3"
                                    />
                                </svg>
                            </button>
                            
                            {/* Print PDF Button */}
                            <button
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-gray-100 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 transition duration-200 ease-in-out"
                                onClick={handlePrint}
                                title={t.print}
                            >
                                <PdfIcon />
                            </button>

                            {/* Export Excel Button */}
                            <button
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-gray-100 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 transition duration-200 ease-in-out"
                                onClick={handleExportExcel}
                                title={t.excel}
                            >
                                <ExcelIcon />
                            </button>

                            {/* Language Selector */}
                            <select
                                className="bg-blue-900 text-white py-2 px-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-300 ease-in-out hover:bg-blue-800 w-24 truncate"
                                value={language}
                                onChange={e => setLanguage(e.target.value)}
                            >
                                {Object.keys(translations).map(lang => <option key={lang} value={lang}>{lang}</option>)}
                            </select>

                            {/* Dark Mode Toggle Button */}
                            <button
                                className="p-2 bg-white dark:bg-gray-700 text-blue-800 dark:text-yellow-300 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105 transition duration-200 ease-in-out"
                                onClick={toggleDarkMode}
                                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                            >
                                {darkMode ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m15.07-6.07-.71.71M6.34 17.66l-.71.71m12.73 0-.71-.71M6.34 6.34l-.71-.71M12 5a7 7 0 100 14A7 7 0 0012 5z" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                </header>
                
                {/* Loop Selection Buttons */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-blue-100 dark:bg-gray-700 rounded-xl shadow-inner-lg sm:flex sm:flex-wrap sm:justify-center">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <button
                            key={i}
                            // Removed w-full for mobile, as grid items will naturally size.
                            // Kept sm:w-28 for desktop width.
                            className={`md:w-48 lg:w-28 px-5 py-2.5 rounded-lg font-medium transition duration-300 ease-in-out transform mb-2 sm:mb-0 sm:w-28
                                ${currentLoop === i
                                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                                    : 'bg-blue-300 dark:bg-gray-600 text-blue-800 dark:text-gray-100 hover:bg-blue-400 dark:hover:bg-gray-500 hover:scale-105 active:bg-blue-500 active:scale-95 cursor-pointer'
                                }
                                ${centralaType === 'FP9000A' && i > 1 ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            onClick={async () => {
                                // Vibrate the phone for 25ms
                                try { Haptics.vibrate({ duration: 25 }); } catch (_) { /* Haptics unavailable on web */ }
                                // Change the active loop
                                handleLoopChange(i);
                            }}
                            disabled={centralaType === 'FP9000A' && i > 1}
                        >
                            {t.loop} {i}
                        </button>
                    ))}
                </div>

                {/* Device Input Section */}
                {isMobile ? (
                    <div className="flex flex-col">
                        {/* Mobile Device Column Tabs */}
                        <div className="flex justify-center gap-2 p-2 bg-blue-100 dark:bg-gray-700 rounded-xl shadow-inner-lg mb-4">
                            {[0, 1, 2].map(colIndex => (
                                <button
                                    key={colIndex}
                                    className={`flex-1 py-2.5 rounded-lg font-medium transition duration-300 ease-in-out transform
                                        ${activeDeviceColumn === colIndex
                                            ? 'bg-blue-600 text-white shadow-lg scale-105'
                                            : 'bg-blue-300 dark:bg-gray-600 text-blue-800 dark:text-gray-100 hover:bg-blue-400 dark:hover:bg-gray-500 hover:scale-105 active:bg-blue-500 active:scale-95 cursor-pointer'
                                        }`}
                                    onClick={async () => {
                                        // Vibrate the phone for 25ms
                                        try { Haptics.vibrate({ duration: 25 }); } catch (_) { /* Haptics unavailable on web */ }
                                        // Scroll to the specified column
                                        scrollToColumn(colIndex);
                                    }} // Calls the scroll function
                                >
                                    {colIndex + 1} {/* Display as 1, 2, 3 */}
                                </button>
                            ))}
                        </div>

                        {/* Swipable Container for Device Columns */}
                        <div
                            className="overflow-x-auto snap-x snap-mandatory hide-scrollbar"
                            ref={mainContentRef}
                            onScroll={handleScroll} // Listens for scroll events to update activeDeviceColumn
                            style={{ touchAction: 'pan-x pan-y' }}
                        >
                            <style>
                                {/* Custom CSS to hide scrollbar for a cleaner look */}
                                {`.hide-scrollbar::-webkit-scrollbar { display: none; }`}
                                {`.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}
                            </style>
                            <div className="flex transition-transform duration-300 ease-in-out">
                                {/* Column 1 (index 0) */}
                                <div className="w-full flex-shrink-0 bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4 snap-center">
                                    {DEVICE_NAMES.slice(0, 6).map((name, i) => (
                                        <InputNumber
                                            key={name}
                                            name={name}
                                            value={devicesLoops[currentLoop - 1][i]}
                                            onChange={e => handleDeviceCountChange(i, e.target.value)}
                                            title={DEVICE_TOOLTIPS[i]}
                                            // onInputFocus prop is removed as it's no longer needed with scroll-snap
                                        />
                                    ))}
                                </div>

                                {/* Column 2 (index 1) */}
                                <div className="w-full flex-shrink-0 bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4 snap-center">
                                    {DEVICE_NAMES.slice(6, 11).map((name, i) => (
                                        <InputNumber
                                            key={name}
                                            name={name}
                                            value={devicesLoops[currentLoop - 1][i + 6]}
                                            onChange={e => handleDeviceCountChange(i + 6, e.target.value)}
                                            title={DEVICE_TOOLTIPS[i + 6]}
                                            // onInputFocus prop is removed
                                        />
                                    ))}
                                </div>

                                {/* Column 3 (index 2) */}
                                <div className="w-full flex-shrink-0 bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4 snap-center">
                                    {DEVICE_NAMES.slice(11, 16).map((name, i) => (
                                        <InputNumber
                                            key={name}
                                            name={name}
                                            value={devicesLoops[currentLoop - 1][i + 11]}
                                            onChange={e => handleDeviceCountChange(i + 11, e.target.value)}
                                            title={DEVICE_TOOLTIPS[i + 11]}
                                            // onInputFocus prop is removed
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Desktop view: original three columns (this part remains unchanged)
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Column 1 */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4">
                            {DEVICE_NAMES.slice(0, 6).map((name, i) => (
                                <InputNumber
                                    key={name}
                                    name={name}
                                    value={devicesLoops[currentLoop - 1][i]}
                                    onChange={e => handleDeviceCountChange(i, e.target.value)}
                                    title={DEVICE_TOOLTIPS[i]}
                                />
                            ))}
                        </div>

                        {/* Column 2 */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4">
                            {DEVICE_NAMES.slice(6, 11).map((name, i) => (
                                <InputNumber
                                    key={name}
                                    name={name}
                                    value={devicesLoops[currentLoop - 1][i + 6]}
                                    onChange={e => handleDeviceCountChange(i + 6, e.target.value)}
                                    title={DEVICE_TOOLTIPS[i + 6]}
                                />
                            ))}
                        </div>

                        {/* Column 3 */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4">
                            {DEVICE_NAMES.slice(11, 16).map((name, i) => (
                                <InputNumber
                                    key={name}
                                    name={name}
                                    value={devicesLoops[currentLoop - 1][i + 11]}
                                    onChange={e => handleDeviceCountChange(i + 11, e.target.value)}
                                    title={DEVICE_TOOLTIPS[i + 11]}
                                />
                            ))}
                        </div>
                    </div>
                )}



                                {/* Summary Panel */}
                <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-4 rounded-xl shadow-lg flex flex-col items-center gap-3">
                    <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left text-lg text-gray-900 dark:text-gray-100">
                            <span>{t.totalDevices}</span>
                            <span className={`sm:ml-2 font-semibold ${totalDevices > 250 ? 'text-red-600 dark:text-red-400' : 'text-blue-900 dark:text-blue-300'}`}>
                                {totalDevices} / 250
                            </span>
                        </div>
                        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left text-lg text-gray-900 dark:text-gray-100">
                            <span>{t.consumption}</span>
                            <span className={`sm:ml-2 font-semibold ${totalConsumption > 700 ? 'text-red-600 dark:text-red-400' : 'text-blue-900 dark:text-blue-300'}`}>
                                {totalConsumption.toFixed(2)} mA / 700 mA
                            </span>
                        </div>
                    <div className="tooltip-container relative group">
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-gray-600 dark:text-gray-400 cursor-help transition duration-200 ease-in-out group-hover:scale-110 group-hover:text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <div className="absolute bottom-full left-1/2 mb-2 w-64 -translate-x-1/2 rounded-md bg-gray-700 px-3 py-2 text-center text-sm text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none sm:left-auto sm:right-0 sm:translate-x-0">
                        {t.infoTooltip}
                    </div>
                </div>
                    </div>
                    {loopIzulators > 100 && (
                        <div className="w-full text-center">
                            <span className="text-red-600 font-bold text-lg uppercase tracking-wide">
                                ⚠ {t.maxIzulatorWarning}
                            </span>
                        </div>
                    )}
                </div>

                {/* Expand/Collapse Button for Mobile */}
                <div className="lg:hidden flex justify-center w-full px-4 mt-2">
                    <button
                        onClick={async () => {
                            // Vibrate the phone for 50ms
                            try { Haptics.vibrate({ duration: 25 }); } catch (_) { /* Haptics unavailable on web */ }
                            // Then, perform the original state change
                            setIsBottomTabsExpanded(!isBottomTabsExpanded);
                        }}
                        className="bg-blue-600 dark:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-700 dark:hover:bg-blue-600 active:bg-blue-800 active:scale-95 transition duration-300 ease-in-out transform hover:scale-105 w-full max-w-xs cursor-pointer"
                    >
                        {isBottomTabsExpanded ? t.hideAdditionalSettings : t.showAdditionalSettings}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-5 w-5 inline-block ml-2 transition-transform duration-300 ${isBottomTabsExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                {/* Wrapper for Calculation Panels and Battery Panel with sliding animation */}
                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isMobile && !isBottomTabsExpanded ? 'max-h-0' : 'max-h-[5000px]'}`}>
                    {/* Added p-4 to give more space around the content within the expandable section, preventing clipping of shadows/borders */}
                    <div className="p-4">
                        {/* Calculation Panels */}
                        {/* Adjusted mb-6 for mobile, and added sm:mb-6 for desktop gap */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:grid mb-6 sm:mb-6">
                        {/* Cable Length & Cross-section */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4">
                            <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-300 mb-4">
                            {t.inputCrossSectionTitle}
                            </h3>
                            <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.crossSection}</label>
                            <select
                                className="bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-200 ease-in-out hover:border-blue-400"
                                value={crossSection}
                                onChange={e => setCrossSection(parseFloat(e.target.value))}
                            >
                                {CROSS_SECTIONS.map(cs => (
                                <option key={cs} value={cs}>
                                    {cs.toFixed(2)} mm²
                                </option>
                                ))}
                            </select>
                            </div>
                            <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.cableLength}</label>
                            <span className="text-blue-900 dark:text-blue-300 font-semibold text-lg">
                                {calculatedCableLength.toFixed(2)} m
                            </span>
                            </div>
                        </div>

                        {/* Input Cable Length & Calculated Cross-section */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4">
                            <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-300 mb-4">
                            {t.inputCableLengthTitle}
                            </h3>
                            <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.cableLength}</label>
                            <div className="flex items-center space-x-2">
                                <input
                                type="number"
                                className="w-28 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 text-right text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-200 ease-in-out hover:border-blue-400"
                                min="0"
                                value={cableLengthInput}
                                onChange={e => {
                                    const v = parseFloat(e.target.value) || 0;
                                    setCableLengthInput(Math.max(0, v));
                                }}
                                />
                                <span className="text-gray-800 dark:text-gray-200">m</span>
                            </div>
                            </div>
                            <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.crossSection}</label>
                            <span
                                className={`text-lg font-semibold ${
                                calculatedCrossSection === -1 ? 'text-red-600 dark:text-red-400' : 'text-blue-900 dark:text-blue-300'
                                }`}
                            >
                                {calculatedCrossSection === -1
                                ? '- mm²'
                                : `${calculatedCrossSection.toFixed(2)} mm²`}
                            </span>
                            </div>
                        </div>
                        </div>

                        {/* Battery & Additional Consumption Panel */}
                        <div className="bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 p-6 rounded-xl shadow-lg space-y-4 sm:block">
                        <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-300 mb-4">
                            {t.batteryAutonomyTitle}
                        </h3>
                        <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.batteryCapacity}</label>
                            <select
                            className="bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-200 ease-in-out hover:border-blue-400"
                            value={batteryCapacity}
                            onChange={e => setBatteryCapacity(parseFloat(e.target.value))}
                            >
                            {Object.entries(BATTERY_CAPACITIES).map(([text, val]) => (
                                <option
                                key={text}
                                value={val}
                                disabled={centralaType === 'FP9000A' ? val > 9 : val < 9}
                                >
                                {text}
                                </option>
                            ))}
                            </select>
                        </div>
                        <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.additionalConsumption}</label>
                            <div className="flex items-center space-x-2">
                            <input
                                type="number"
                                className="w-28 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 text-right text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-200 ease-in-out hover:border-blue-400"
                                min="0"
                                value={userCurrent}
                                onChange={e => {
                                    const raw = parseFloat(e.target.value);
                                    const safe = Math.max(0, isNaN(raw) ? 0 : raw);
                                    setUserCurrent(safe);
                                }}
                                />
                            <span className="text-gray-800 dark:text-gray-200">mA</span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <label className="text-gray-800 dark:text-gray-200 font-medium">{t.workHours}</label>
                            <span className="text-blue-900 dark:text-blue-300 font-semibold text-lg">{workHours}</span>
                        </div>
                        </div>
                    </div>
                </div>

                <div
                className="bottom-0 flex justify-center items-center bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500 text-white p-4 rounded-lg shadow-2xl relative transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-3xl cursor-pointer"
                onClick={async () => {
                    // Vibrate the phone for 50ms
                    try { Haptics.vibrate({ duration: 50 }); } catch (_) { /* Haptics unavailable on web */ }
                    // Show the attributions modal
                    setShowAttributions(true);
                }}
            >
                <div className="flex flex-col items-center text-center">
                    <span className="text-xl font-semibold tracking-wide">
                        DMTech Ltd. Loop Calculator WebApp
                    </span>
                    <span className="text-sm font-light mt-1 animate-pulse">
                        Version 1.1.1
                    </span>
                </div>
            </div>

            <Suspense>
                {showAttributions && <AttributionsModal onClose={() => setShowAttributions(false)} />}
            </Suspense>

            </div>
            {/* Mobile Side Panel - Moved here to cover full viewport */}
            <div
                ref={sidebarRef}
                className={`fixed inset-y-0 left-0 w-64 bg-blue-800 dark:bg-gray-900 text-white p-4 shadow-lg transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out md:hidden z-40`} // CHANGED TO md:hidden
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">Menu</h2>
                    <button
                        className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                        onClick={async () => {
                            // Vibrate the phone for 50ms
                            try { Haptics.vibrate({ duration: 30 }); } catch (_) { /* Haptics unavailable on web */ }
                            // Close the mobile menu
                            setIsMobileMenuOpen(false);
                        }}
                    >
                        <svg
                            className="h-6 w-6 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
                <div className="flex flex-col space-y-4">
                    <select
                        className="bg-blue-900 dark:bg-gray-800 text-white py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-300 ease-in-out hover:bg-blue-800"
                        value={centralaType}
                        onChange={handleCentralaChange}
                    >
                        <option value="FP9000A">FP9000A</option>
                        <option value="FP9000A-6">FP9000A-6</option>
                    </select>
                    <span className="text-xl font-semibold">{t.loop}: {currentLoop}</span>
                    
                    {/* Mobile Action Buttons */}
                    <button
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 active:bg-blue-500 active:scale-95 hover:scale-[1.02] transition duration-200 ease-in-out cursor-pointer"
                        onClick={handleNew}
                        title={t.new}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V7l-6-6H7a2 2 0 00-2 2v14a2 2 0 002 2z"/><path strokeLinecap="round" strokeLinejoin="round" d="M13 3v6h6"/></svg>
                        {t.new}
                    </button>
                    <label
                        htmlFor="file-upload-mobile"
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 hover:scale-[1.02] cursor-pointer transition duration-200 ease-in-out"
                        title={t.load}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h6l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
                        {t.load}
                    </label>
                    <input
                        id="file-upload-mobile"
                        type="file"
                        accept=".json"
                        onChange={handleLoad}
                        className="hidden"
                    />
                    <button
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 active:bg-blue-500 active:scale-95 hover:scale-[1.02] transition duration-200 ease-in-out cursor-pointer"
                        onClick={handleSave}
                        title={t.save}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-8H7v8M7 3v5h8V3"/></svg>
                        {t.save}
                    </button>
                    <button
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 active:bg-blue-500 active:scale-95 hover:scale-[1.02] transition duration-200 ease-in-out cursor-pointer"
                        onClick={handlePrint}
                        title={t.print}
                    >
                        <PdfIcon width={24} height={24} /> <span className="ml-2">{t.print}</span>
                    </button>
                    <button
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 active:bg-blue-500 active:scale-95 hover:scale-[1.02] transition duration-200 ease-in-out cursor-pointer"
                        onClick={handleExportExcel}
                        title={t.excel}
                    >
                        <ExcelIcon width={24} height={24} /> <span className="ml-2">{t.excel}</span>
                    </button>
                    <select
                        className="bg-blue-900 dark:bg-gray-800 text-white py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-300 ease-in-out hover:bg-blue-800"
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                    >
                        {Object.keys(translations).map(lang => <option key={lang} value={lang}>{lang}</option>)}
                    </select>

                    {/* Dark Mode Toggle (mobile) */}
                    <button
                        className="flex items-center p-3 bg-blue-700 dark:bg-gray-700 text-white rounded-md shadow-md hover:bg-blue-600 dark:hover:bg-gray-600 active:scale-95 hover:scale-[1.02] transition duration-200 ease-in-out cursor-pointer"
                        onClick={toggleDarkMode}
                    >
                        {darkMode ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m15.07-6.07-.71.71M6.34 17.66l-.71.71m12.73 0-.71-.71M6.34 6.34l-.71-.71M12 5a7 7 0 100 14A7 7 0 0012 5z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                            </svg>
                        )}
                        {darkMode ? 'Light Mode' : 'Dark Mode'}
                    </button>
                </div>
            </div>

            {/* Overlay for mobile menu - Moved here and increased z-index */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" // CHANGED TO md:hidden
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}
        </div>
    );
}



// Helper class for time calculations, similar to .NET's TimeSpan
class TimeSpan {
    constructor(ms) {
        this.totalMilliseconds = ms;
        this.totalSeconds = ms / 1000;
        this.totalMinutes = ms / (1000 * 60);
        this.totalHours = ms / (1000 * 60 * 60);

        this.minutes = Math.floor(this.totalMinutes) % 60;
        this.seconds = Math.floor(this.totalSeconds) % 60;
    }

    static fromHours(hours) {
        return new TimeSpan(hours * 60 * 60 * 1000);
    }
}



App.propTypes = {
  name:     PropTypes.string.isRequired,
  value:    PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  title:    PropTypes.string,
  width:    PropTypes.number,
  height:   PropTypes.number
};



InputNumber.propTypes = {
  name:     PropTypes.string.isRequired,
  value:    PropTypes.oneOfType([
               PropTypes.string,
               PropTypes.number
             ]).isRequired,
  onChange: PropTypes.func.isRequired,
  title:    PropTypes.string.isRequired
};

export default App;